/**
 * The Move modal's "invite to next interview" draft.
 *
 * Own file because taActionBoardApi.ts is at its line budget; re-exported from there, so
 * `import * as api from '../../services/taActionBoardApi'` keeps reaching it.
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

const BASE = `${PROXY_URL}/api/ta-action-board`;

/**
 * One live interview link a recruiter may pick for a role+stage. A stage can carry several (one
 * per interviewer), and what tells them apart is who interviews — so that is what is shipped.
 */
export interface InterviewLinkOption {
  id: string;
  name: string;
  meetingType: '1:1' | 'panel';
  minutes: number | null;
  interviewers: { email: string; name: string }[];
  updatedAt: string | null;
}

export interface InviteDraft {
  subject: string;
  body: string;
  /**
   * 'link' = the link's deliberately customised wording (Interview Links page);
   * 'library' = the stage's Email Library template; 'default' = the fallback assembly.
   */
  source: 'link' | 'library' | 'default';
  /** The Email Library key behind a 'library' draft — the deep-link target. Null otherwise. */
  templateKey?: string | null;
  /** Minted per-candidate booking URL, or '' when no interview link exists yet. */
  bookingUrl: string;
  candidateEmail: string;
  language: 'de' | 'en';
  meta: {
    linkFound: boolean;
    /** The link this draft — and its booking URL — was built on. Send it back on a re-draft. */
    linkId: string | null;
    /**
     * Every live link for this role+stage, newest first. The picker shows only when there is
     * more than one. Optional: a proxy that predates it sends none, which means "no choice".
     */
    linkOptions?: InterviewLinkOption[];
    /** The role is closed in Lever, so no link is offered at all. */
    roleClosed?: boolean;
    meetingType: '1:1' | 'panel' | null;
    panelsAvailable: boolean;
    directoryAvailable: boolean;
    stageName: string;
    nextInterviewers: { email: string; name: string; title: string }[];
  };
}

/**
 * Draft the "invite to next interview" email for the Move modal. Brings together
 * the target-stage interview link (booking URL + next interviewers), Lever
 * (previous interviewers) and the directory (titles). Drafts only — it never
 * emails the candidate.
 *
 * `linkId` is an override, never required: without it the proxy derives the default (the
 * recruiter's own link, else the newest), so the common case costs no click.
 */
export async function fetchInviteDraft(payload: {
  opportunityId: string;
  postingId: string;
  targetStageId: string;
  stageName?: string;
  roleTitle?: string;
  boardEmail: string;
  senderName?: string;
  language: 'de' | 'en';
  linkId?: string | null;
}): Promise<InviteDraft> {
  const res = await authenticatedFetch(`${BASE}/invite-draft`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return res.json() as Promise<InviteDraft>;
}
